/**
 * WelcomeCustomizeDrawer
 *
 * Two sections:
 *   1. Layout – switch between "Tiles" (grid) and "Sidebar" (left nav) layouts.
 *   2. Icons  – replace each module tile's image/icon with a predefined Ant
 *               Design icon or a custom uploaded image.
 *
 * Layout changes save to configs/app-settings.welcomeLayout via the caller's
 * saveLayout() callback. Icon changes save immediately per module.
 */
import React, { useState } from 'react'
import {
	Drawer,
	Row,
	Col,
	Card,
	Tabs,
	Upload,
	Button,
	Tooltip,
	message,
	Spin,
	Tag,
	Typography,
	Popconfirm,
	Divider,
} from 'antd'
import {
	// People / HR
	UserOutlined,
	TeamOutlined,
	UsergroupAddOutlined,
	SolutionOutlined,
	IdcardOutlined,
	ContactsOutlined,
	// Organization / Building
	BankOutlined,
	HomeOutlined,
	GlobalOutlined,
	ApartmentOutlined,
	ClusterOutlined,
	// Time / Attendance
	ClockCircleOutlined,
	CalendarOutlined,
	ScheduleOutlined,
	FieldTimeOutlined,
	HourglassOutlined,
	// Check / Activity
	CheckCircleOutlined,
	LoginOutlined,
	LogoutOutlined,
	SwapOutlined,
	// Charts / Reports
	BarChartOutlined,
	LineChartOutlined,
	PieChartOutlined,
	FundOutlined,
	RiseOutlined,
	// Safety / Security
	SafetyCertificateOutlined,
	LockOutlined,
	SecurityScanOutlined,
	FileProtectOutlined,
	SafetyOutlined,
	// Management / Settings
	SettingOutlined,
	AppstoreOutlined,
	DatabaseOutlined,
	ToolOutlined,
	// Leaves / Nature
	FileTextOutlined,
	ReconciliationOutlined,
	BookOutlined,
	CoffeeOutlined,
	// Upload / Reset
	UploadOutlined,
	UndoOutlined,
	DeleteOutlined,
	CheckOutlined,
} from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import { httpDelete, httpPatch } from '../../common/http.service'
import { useThemeColors } from '../../common/ThemeContext'


const { Text } = Typography

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WelcomeModuleIcon {
	type: 'icon' | 'image'
	value: string
	storagePath?: string | null
}

export interface WelcomeModuleDef {
	key: string
	defaultLabel: string
	defaultIcon: React.ComponentType<any>
	defaultImg?: string
}

// ─── Predefined icon catalogue ────────────────────────────────────────────────

const PREDEFINED_ICONS: { name: string; component: React.ComponentType<any> }[] = [
	{ name: 'UserOutlined', component: UserOutlined },
	{ name: 'TeamOutlined', component: TeamOutlined },
	{ name: 'UsergroupAddOutlined', component: UsergroupAddOutlined },
	{ name: 'SolutionOutlined', component: SolutionOutlined },
	{ name: 'IdcardOutlined', component: IdcardOutlined },
	{ name: 'ContactsOutlined', component: ContactsOutlined },
	{ name: 'BankOutlined', component: BankOutlined },
	{ name: 'HomeOutlined', component: HomeOutlined },
	{ name: 'GlobalOutlined', component: GlobalOutlined },
	{ name: 'ApartmentOutlined', component: ApartmentOutlined },
	{ name: 'ClusterOutlined', component: ClusterOutlined },
	{ name: 'ClockCircleOutlined', component: ClockCircleOutlined },
	{ name: 'CalendarOutlined', component: CalendarOutlined },
	{ name: 'ScheduleOutlined', component: ScheduleOutlined },
	{ name: 'FieldTimeOutlined', component: FieldTimeOutlined },
	{ name: 'HourglassOutlined', component: HourglassOutlined },
	{ name: 'CheckCircleOutlined', component: CheckCircleOutlined },
	{ name: 'LoginOutlined', component: LoginOutlined },
	{ name: 'LogoutOutlined', component: LogoutOutlined },
	{ name: 'SwapOutlined', component: SwapOutlined },
	{ name: 'BarChartOutlined', component: BarChartOutlined },
	{ name: 'LineChartOutlined', component: LineChartOutlined },
	{ name: 'PieChartOutlined', component: PieChartOutlined },
	{ name: 'FundOutlined', component: FundOutlined },
	{ name: 'RiseOutlined', component: RiseOutlined },
	{ name: 'SafetyCertificateOutlined', component: SafetyCertificateOutlined },
	{ name: 'LockOutlined', component: LockOutlined },
	{ name: 'SecurityScanOutlined', component: SecurityScanOutlined },
	{ name: 'FileProtectOutlined', component: FileProtectOutlined },
	{ name: 'SafetyOutlined', component: SafetyOutlined },
	{ name: 'SettingOutlined', component: SettingOutlined },
	{ name: 'AppstoreOutlined', component: AppstoreOutlined },
	{ name: 'DatabaseOutlined', component: DatabaseOutlined },
	{ name: 'ToolOutlined', component: ToolOutlined },
	{ name: 'FileTextOutlined', component: FileTextOutlined },
	{ name: 'ReconciliationOutlined', component: ReconciliationOutlined },
	{ name: 'BookOutlined', component: BookOutlined },
	{ name: 'CoffeeOutlined', component: CoffeeOutlined },
]

// ─── Icon name → component resolver (used when rendering saved icon names) ────

const ICON_MAP: Record<string, React.ComponentType<any>> = Object.fromEntries(
	PREDEFINED_ICONS.map((i) => [i.name, i.component]),
)

export function resolveIconComponent(
	name: string,
): React.ComponentType<any> | null {
	return ICON_MAP[name] ?? null
}

/** Resolve custom / default image or icon for a module tile (Welcome + sidebar Menu). */
export function resolvedTileAsset(
	mod: WelcomeModuleDef,
	icons: Record<string, WelcomeModuleIcon>,
): { type: 'img'; src: string } | { type: 'icon'; component: React.ComponentType<any> } {
	const override = icons[mod.key]
	if (override?.type === 'image' && override.value) {
		return { type: 'img', src: override.value }
	}
	if (override?.type === 'icon' && override.value) {
		const Comp = resolveIconComponent(override.value)
		if (Comp) return { type: 'icon', component: Comp }
	}
	if (mod.defaultImg) return { type: 'img', src: mod.defaultImg }
	return { type: 'icon', component: mod.defaultIcon }
}

// ─── Single module editor card ────────────────────────────────────────────────

interface ModuleEditorProps {
	module: WelcomeModuleDef
	current: WelcomeModuleIcon | null
	onChange: (mod: WelcomeModuleDef, icon: WelcomeModuleIcon | null) => void
}

function ModuleEditor({ module, current, onChange }: ModuleEditorProps) {
	const { primary } = useThemeColors()
	const [uploading, setUploading] = useState(false)
	const [savingIcon, setSavingIcon] = useState<string | null>(null)
	const [fileList, setFileList] = useState<UploadFile[]>([])

	const DefaultIcon = module.defaultIcon
	const isCustomised = current !== null

	// ── Preview tile ────────────────────────────────────────────────────────
	const Preview = () => {
		if (current?.type === 'image') {
			return (
				<img
					src={current.value}
					alt={module.defaultLabel}
					style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
				/>
			)
		}
		if (current?.type === 'icon') {
			const Icon = resolveIconComponent(current.value)
			if (Icon)
				return (
					<Icon style={{ fontSize: 44, color: primary }} />
				)
		}
		// Default: original image or fallback icon
		if (module.defaultImg) {
			return (
				<img
					src={module.defaultImg}
					alt={module.defaultLabel}
					style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
				/>
			)
		}
		return <DefaultIcon style={{ fontSize: 44, color: primary }} />
	}

	// ── Upload custom image ──────────────────────────────────────────────────
	const beforeUpload = (file: File) => {
		const ok =
			['image/jpeg', 'image/png', 'image/webp'].includes(file.type) &&
			file.size <= 5 * 1024 * 1024
		if (!ok) {
			message.error('JPEG/PNG/WebP only, max 5 MB')
			return Upload.LIST_IGNORE
		}
		return false
	}

	const handleUpload = async () => {
		const orig = fileList[0]?.originFileObj
		if (!orig) return
		try {
			setUploading(true)
			const fd = new FormData()
			fd.append('icon', orig as File)
			const res: any = await httpPatch(
				`/api/v1/admin/app-settings/module-icon/${module.key}`,
				fd,
				{ 'Content-Type': 'multipart/form-data' },
			)
			onChange(module, res?.data ?? null)
			setFileList([])
			message.success(`${module.defaultLabel} icon updated`)
		} catch (e: any) {
			message.error(e?.response?.data?.message || 'Upload failed')
		} finally {
			setUploading(false)
		}
	}

	// ── Pick predefined icon ─────────────────────────────────────────────────
	const handlePickIcon = async (iconName: string) => {
		try {
			setSavingIcon(iconName)
			await httpPatch(
				`/api/v1/admin/app-settings/module-icon/${module.key}/predefined`,
				{ iconName },
			)
			onChange(module, { type: 'icon', value: iconName, storagePath: null })
			message.success(`${module.defaultLabel} icon updated`)
		} catch (e: any) {
			message.error(e?.response?.data?.message || 'Failed to save icon')
		} finally {
			setSavingIcon(null)
		}
	}

	// ── Reset to default ─────────────────────────────────────────────────────
	const handleReset = async () => {
		try {
			await httpDelete(`/api/v1/admin/app-settings/module-icon/${module.key}`)
			onChange(module, null)
			message.success(`${module.defaultLabel} reset to default`)
		} catch (e: any) {
			message.error(e?.response?.data?.message || 'Reset failed')
		}
	}

	return (
		<Card
			size='small'
			style={{ borderRadius: 12, height: '100%' }}
			styles={{ body: { padding: 16 } }}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 12,
					height: '100%',
				}}
			>
				{/* Header row */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Text strong style={{ fontSize: 13 }}>
						{module.defaultLabel}
					</Text>
					{isCustomised && (
						<Tag color='blue' style={{ marginRight: 0, fontSize: 11 }}>
							Customised
						</Tag>
					)}
				</div>

				{/* Preview box */}
				<div
					style={{
						width: '100%',
						height: 100,
						borderRadius: 10,
						border: '1px dashed #d0d5dd',
						backgroundColor: '#f5f7fa',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						overflow: 'hidden',
					}}
				>
					<Preview />
				</div>

				{/* Edit tabs */}
				<Tabs
					size='small'
					items={[
						{
							key: 'icons',
							label: 'Pick Icon',
							children: (
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: 'repeat(6, 1fr)',
										gap: 6,
										maxHeight: 180,
										overflowY: 'auto',
										paddingRight: 2,
									}}
								>
									{PREDEFINED_ICONS.map((pi) => {
										const Icon = pi.component
										const isActive =
											current?.type === 'icon' &&
											current.value === pi.name
										return (
											<Tooltip
												key={pi.name}
												title={pi.name.replace('Outlined', '')}
											>
												<button
													type='button'
													onClick={() => handlePickIcon(pi.name)}
													disabled={savingIcon !== null}
													style={{
														width: '100%',
														aspectRatio: '1',
														border: isActive
															? `2px solid ${primary}`
															: '1px solid #e5e7eb',
														borderRadius: 8,
														backgroundColor: isActive
															? `${primary}12`
															: '#fff',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														cursor: 'pointer',
														padding: 0,
														position: 'relative',
													}}
												>
													{savingIcon === pi.name ? (
														<Spin size='small' />
													) : (
														<>
															<Icon
																style={{
																	fontSize: 18,
																	color: isActive
																		? primary
																		: '#6b7280',
																}}
															/>
															{isActive && (
																<CheckOutlined
																	style={{
																		position: 'absolute',
																		bottom: 2,
																		right: 2,
																		fontSize: 9,
																		color: primary,
																	}}
																/>
															)}
														</>
													)}
												</button>
											</Tooltip>
										)
									})}
								</div>
							),
						},
						{
							key: 'upload',
							label: 'Upload Image',
							children: (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 10,
									}}
								>
									<Text type='secondary' style={{ fontSize: 12 }}>
										JPEG, PNG, or WebP — max 5 MB
									</Text>
									<Upload
										listType='picture'
										fileList={fileList}
										beforeUpload={beforeUpload}
										onChange={(info) =>
											setFileList([...info.fileList].slice(-1))
										}
										onRemove={() => setFileList([])}
										maxCount={1}
										accept='image/jpeg,image/png,image/webp'
									>
										<Button
											size='small'
											icon={<UploadOutlined />}
										>
											Choose File
										</Button>
									</Upload>
									<Button
										type='primary'
										size='small'
										icon={<UploadOutlined />}
										loading={uploading}
										disabled={
											fileList.length === 0 ||
											!fileList[0]?.originFileObj
										}
										onClick={handleUpload}
										style={{ alignSelf: 'flex-start' }}
									>
										Upload
									</Button>
								</div>
							),
						},
					]}
				/>

				{/* Reset */}
				{isCustomised && (
					<Popconfirm
						title={`Reset "${module.defaultLabel}" to default?`}
						onConfirm={handleReset}
						okText='Reset'
						cancelText='Cancel'
						okButtonProps={{ danger: true }}
					>
						<Button
							size='small'
							icon={<UndoOutlined />}
							danger
							style={{ alignSelf: 'flex-start' }}
						>
							Reset to default
						</Button>
					</Popconfirm>
				)}
			</div>
		</Card>
	)
}

// ─── Layout picker thumbnails ─────────────────────────────────────────────────

const TilesThumbnail = () => (
	<svg viewBox='0 0 80 56' width='100%' height='100%'>
		{[0, 1, 2, 3].map((i) => (
			<rect
				key={i}
				x={4 + (i % 2) * 38}
				y={4 + Math.floor(i / 2) * 26}
				width={34}
				height={22}
				rx={3}
				fill='currentColor'
				opacity={0.18}
				stroke='currentColor'
				strokeWidth={1.5}
				strokeOpacity={0.4}
			/>
		))}
	</svg>
)

const SidebarThumbnail = () => (
	<svg viewBox='0 0 80 56' width='100%' height='100%'>
		{/* Sidebar */}
		<rect x={4} y={4} width={20} height={48} rx={3} fill='currentColor' opacity={0.25} />
		{/* Menu items */}
		{[10, 20, 30].map((y) => (
			<rect key={y} x={7} y={y} width={14} height={4} rx={2} fill='currentColor' opacity={0.5} />
		))}
		{/* Content area */}
		<rect x={28} y={4} width={48} height={48} rx={3} fill='currentColor' opacity={0.1} stroke='currentColor' strokeWidth={1.2} strokeOpacity={0.3} />
		{/* Content lines */}
		<rect x={32} y={12} width={36} height={5} rx={2} fill='currentColor' opacity={0.35} />
		<rect x={32} y={21} width={28} height={3} rx={2} fill='currentColor' opacity={0.2} />
		<rect x={32} y={27} width={22} height={3} rx={2} fill='currentColor' opacity={0.2} />
	</svg>
)

export type WelcomeLayout = 'tiles' | 'sidebar'

interface LayoutPickerProps {
	current: WelcomeLayout
	onChange: (layout: WelcomeLayout) => void
}

function LayoutPicker({ current, onChange }: LayoutPickerProps) {
	const { primary } = useThemeColors()

	const options: { key: WelcomeLayout; label: string; desc: string; Thumb: React.FC }[] = [
		{
			key: 'tiles',
			label: 'Tiles',
			desc: 'Grid of clickable image / icon cards',
			Thumb: TilesThumbnail,
		},
		{
			key: 'sidebar',
			label: 'Sidebar',
			desc: 'Left navigation menu + detail panel',
			Thumb: SidebarThumbnail,
		},
	]

	return (
		<Row gutter={16}>
			{options.map(({ key, label, desc, Thumb }) => {
				const active = current === key
				return (
					<Col key={key} xs={12}>
						<button
							type='button'
							onClick={() => onChange(key)}
							style={{
								width: '100%',
								border: active ? `2px solid ${primary}` : '2px solid #e5e7eb',
								borderRadius: 12,
								background: active ? `${primary}08` : '#fff',
								cursor: 'pointer',
								padding: '16px 16px 12px',
								textAlign: 'center',
								transition: 'border 0.15s, background 0.15s',
								boxShadow: active ? `0 0 0 3px ${primary}22` : 'none',
							}}
						>
							<div
								style={{
									color: active ? primary : '#9ca3af',
									height: 64,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									marginBottom: 10,
								}}
							>
								<Thumb />
							</div>
							<Typography.Text
								strong={active}
								style={{ fontSize: 14, color: active ? primary : '#374151', display: 'block' }}
							>
								{label}
							</Typography.Text>
							<Typography.Text
								type='secondary'
								style={{ fontSize: 11, lineHeight: 1.4 }}
							>
								{desc}
							</Typography.Text>
						</button>
					</Col>
				)
			})}
		</Row>
	)
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

interface WelcomeCustomizeDrawerProps {
	open: boolean
	onClose: () => void
	modules: WelcomeModuleDef[]
	moduleIcons: Record<string, WelcomeModuleIcon>
	onIconsChange: (updated: Record<string, WelcomeModuleIcon>) => void
	layout: WelcomeLayout
	onLayoutChange: (layout: WelcomeLayout) => void
}

export function WelcomeCustomizeDrawer({
	open,
	onClose,
	modules,
	moduleIcons,
	onIconsChange,
	layout,
	onLayoutChange,
}: WelcomeCustomizeDrawerProps) {
	const handleChange = (
		mod: WelcomeModuleDef,
		icon: WelcomeModuleIcon | null,
	) => {
		const next = { ...moduleIcons }
		if (icon === null) {
			delete next[mod.key]
		} else {
			next[mod.key] = icon
		}
		onIconsChange(next)
	}

	return (
		<Drawer
			title={
				<span>
					<DeleteOutlined style={{ marginRight: 8, opacity: 0.5 }} />
					Customize Welcome Page
				</span>
			}
			open={open}
			onClose={onClose}
			width={760}
			styles={{ body: { padding: '20px 24px', backgroundColor: '#f9fafb' } }}
			extra={
				<Button onClick={onClose} size='small'>
					Done
				</Button>
			}
		>
			{/* ── Section 1: Layout ─────────────────────────────────────────── */}
			<Typography.Title level={5} style={{ margin: '0 0 4px' }}>
				1 · Layout
			</Typography.Title>
			<Typography.Text type='secondary' style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
				Choose how modules are presented on the welcome page. Changes apply immediately.
			</Typography.Text>
			<LayoutPicker current={layout} onChange={onLayoutChange} />

			<Divider />

			{/* ── Section 2: Icons ──────────────────────────────────────────── */}
			<Typography.Title level={5} style={{ margin: '0 0 4px' }}>
				2 · Module Icons
			</Typography.Title>
			<Typography.Text type='secondary' style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
				Pick a predefined icon or upload a custom image for each module tile.
				Changes save immediately.
			</Typography.Text>
			<Row gutter={[16, 16]}>
				{modules.map((mod) => (
					<Col key={mod.key} xs={24} sm={12} md={8}>
						<ModuleEditor
							module={mod}
							current={moduleIcons[mod.key] ?? null}
							onChange={handleChange}
						/>
					</Col>
				))}
			</Row>
		</Drawer>
	)
}
